import { action, computed, makeObservable, observable, reaction } from 'mobx'

import { DefaultLandingPage, NotificationType } from '~/client/graph'
import mobileRoutes from '~/client/src/mobile/constants/mobileRoutes'
import MobileRootStore from '~/client/src/mobile/stores/MobileRoot.store'
import {
  bareRoutes,
  getURLParam,
  isProjectDeepLink,
} from '~/client/src/shared/constants/commonRoutes'
import BaseNotification from '~/client/src/shared/models/Notification'
import Photo from '~/client/src/shared/models/Photo'
import User from '~/client/src/shared/models/User'
import CommonStore from '~/client/src/shared/stores/ui/Common.store'
import {
  isDeliveryType,
  isPermitType,
  isWorkflowMessageType,
} from '~/client/src/shared/types/NotificationTypes'
import {
  onOverlayChange,
  resetOverlays,
} from '~/client/src/shared/utils/overlayNotifier'

import NativeIntegrator from '../../integration/NativeIntegrator/NativeIntegrator'
import MobileInitialState from '../MobileInitialState'

export default class MobileCommonStore extends CommonStore {
  @observable public isSidebarOpen = false
  public bottomNavBarOverride: (state: boolean) => void | null = null
  public sidebarOverride: () => void | null = null
  @observable public shouldShowBottomNavBar: boolean = true
  @observable public shouldShowHeader = true
  @observable public shouldHideNavBar = false
  @observable public isBottomNavBarOverridden: boolean = false
  public readonly isNativeMode: boolean =
    typeof window !== 'undefined' && !!window.ReactNativeWebView

  @observable private mountedNavBarCount: number = 0

  private navBarRef = null

  public constructor(
    protected state: MobileInitialState,
    protected rootStore: MobileRootStore,
  ) {
    super(state, rootStore)
    makeObservable(this)
    new NativeIntegrator(this)

    onOverlayChange(hasOpenOverlays => {
      if (hasOpenOverlays) {
        this.hideBottomNavBar()
      } else {
        this.showBottomNavBar()
      }
    })

    this.addNavigationListener(() => {
      resetOverlays()
    })

    // Push the effective native-navbar visibility to the native shell whenever
    // either input changes. The shell's own navbar is the only visible chrome
    // in the reserved 60px slot; the web renders a transparent spacer there.
    reaction(
      () => this.effectiveBottomNavBarVisible,
      visible => {
        this.bottomNavBarOverride?.(visible)
      },
    )
  }

  @computed
  public get isBottomNavBarMounted(): boolean {
    return this.mountedNavBarCount > 0
  }

  @computed
  private get effectiveBottomNavBarVisible(): boolean {
    return this.isBottomNavBarMounted && this.shouldShowBottomNavBar
  }

  @action.bound
  public registerBottomNavBar() {
    this.mountedNavBarCount++
    // Push the new effective state directly to the native override.
    // The reaction on `effectiveBottomNavBarVisible` should also catch
    // this change, but in the WebView-remount path we've observed it
    // not firing reliably (STX1-501). Explicit notify here is
    // deterministic; the duplicate fire when the reaction does run is
    // harmless — same payload, native idempotently sets its
    // `shouldShowBottomNavBar`.
    this.bottomNavBarOverride?.(this.effectiveBottomNavBarVisible)
  }

  @action.bound
  public unregisterBottomNavBar() {
    this.mountedNavBarCount = Math.max(0, this.mountedNavBarCount - 1)
    this.bottomNavBarOverride?.(this.effectiveBottomNavBarVisible)
  }

  @action.bound
  public showBottomNavBar() {
    this.shouldShowBottomNavBar = true
  }

  @action.bound
  public hideBottomNavBar() {
    this.shouldShowBottomNavBar = false
  }

  /**
   * Overriding allows an external entity (e.g. native app) to control the sidebar.
   * This is useful if the native app wants to display its own sidebar.
   * @param callback Each time `toggleSidebar` is called, this callback will be called instead, and the actual sidebar will remain hidden. The override is removed by passing `null` as the callback.
   */
  @action.bound
  public overrideSidebar(callback: null | (() => void)) {
    this.sidebarOverride = callback
  }

  /**
   * Overriding allows an external entity (e.g. native app) to control the bottom nav bar.
   * The callback is invoked on install with the current effective visibility so the native
   * shell can sync, and then on every change to effective visibility via:
   *   - the `effectiveBottomNavBarVisible` reaction in the constructor (overlay-driven
   *     `shouldShowBottomNavBar` flips, generally), and
   *   - explicit pushes from `registerBottomNavBar` / `unregisterBottomNavBar` (mount-
   *     driven changes — see STX1-501).
   *
   * The install fire intentionally uses `effective` so that footerless routes
   * (mountedNavBarCount=0, shouldShow=true → effective=false) tell the native shell to
   * hide its chrome at boot. Routes that DO mount a BottomNavigationNavBar bump
   * mountedNavBarCount on `componentDidMount`, and the register action pushes the new
   * effective state to the override directly (without depending on the MobX reaction,
   * which has been observed not to fire on WebView-remount in struxhub.mobile.native).
   */
  @action.bound
  public overrideBottomNavBar(callback: null | ((state: boolean) => void)) {
    this.bottomNavBarOverride = callback
    this.isBottomNavBarOverridden = !!callback
    callback?.(this.effectiveBottomNavBarVisible)
  }

  @action.bound
  public showAppHeader() {
    this.shouldShowHeader = true
  }

  @action.bound
  public hideAppHeader() {
    this.shouldShowHeader = false
  }

  public displayAuthSuccessRoute = () => {
    // Don't redirect when in webview, let the given route handle it
    if (window.ReactNativeWebView) return

    // Follow the user's original deep link (mobile web post-login fly-over),
    // replaying the full captured destination verbatim so no URL component is
    // dropped (STX1-582); otherwise land on the projects picker. Gate and
    // replay both read initDeepLink so they can't diverge (e.g. after the
    // logout reset clears it).
    const path = (this.state.initDeepLink || '').split('?')[0].split('#')[0]
    if (isProjectDeepLink(path)) {
      this._displayView(this.state.initDeepLink)
      return
    }

    this.displayProjects()
  }

  @action.bound
  public toggleSidebar(evt?: any) {
    evt?.stopPropagation()

    if (this.sidebarOverride) {
      this.sidebarOverride()
      return
    }

    if (this.root.auth.isAuthenticated) {
      this.isSidebarOpen = !this.isSidebarOpen
    }
  }

  public toggleNavBar(evt?: any) {
    this.setNavBar(evt)
  }

  public get isFullHeight(): boolean {
    return this.shouldHideNavBar
  }

  public showNavBar = (evt?: any) => {
    this.setNavBar(evt, false)
  }

  public setNavBarRef = ref => {
    this.navBarRef = ref
  }

  @action.bound
  private setNavBar(evt: any, value?: boolean) {
    if (evt) {
      evt.stopPropagation()
    }

    if (value === this.shouldHideNavBar) {
      return Promise.resolve()
    }

    if (this.navBarRef === null) {
      console.warn(
        'WARNING: common.setNavBar called without navBar ref. All Promises will immediate resolve.',
      )
      return Promise.resolve()
    }

    let animEnded
    const animPromise = new Promise(r => (animEnded = r))
    const handleAnimEnd = () => {
      animEnded()
      this.navBarRef.removeEventListener('transitionend', handleAnimEnd)
    }

    this.navBarRef.addEventListener('transitionend', handleAnimEnd)
    this.shouldHideNavBar = value !== undefined ? value : !this.shouldHideNavBar
    return animPromise
  }

  @action.bound
  public hideNavBar() {
    this.shouldHideNavBar = true
  }

  public get isActivityListDisplayed() {
    return false
  }

  public get isDeliveryListDisplayed() {
    return location.pathname.includes(bareRoutes.DELIVERIES)
  }

  public get isLogisticsDisplayed() {
    return location.pathname.includes(bareRoutes.HOME)
  }

  public get isFormsDisplayed() {
    return location.pathname.includes(bareRoutes.FORMS)
  }

  public get isUsersDirectoryDisplayed() {
    return location.pathname.includes(bareRoutes.USERS_DIRECTORY)
  }

  public get isPhotosDisplayed() {
    // TODO: Share-002
    const { pathname } = this.history.location
    return pathname === mobileRoutes.DOCUMENTS
  }

  public get isNotificationsDisplayed() {
    return location.pathname.includes(bareRoutes.NOTIFICATIONS)
  }

  public get isQRCodesDisplayed() {
    return location.pathname.includes(bareRoutes.QR_CODES)
  }

  public get isEquipmentDisplayed() {
    return location.pathname.includes(bareRoutes.EQUIPMENT)
  }

  public get isStruxTagsDisplayed() {
    return location.pathname.includes(bareRoutes.STRUXTAGS)
  }

  public get isMaterialResourcesDisplayed() {
    return location.pathname.includes(bareRoutes.MATERIAL_RESOURCES)
  }

  public get isLocationsDisplayed() {
    return location.pathname.includes(bareRoutes.LOCATIONS)
  }

  public get isChatDisplayed(): boolean {
    // TODO: Share-002
    return this.history.location.pathname === mobileRoutes.CHAT
  }

  public get availableAppRoutes(): string[] {
    const {
      isDeliveriesDisabled,
      isWorkflowsDisabled,
      isEquipmentDisabled,
      isStruxTagsDisabled,
      isMaterialResourcesDisabled,
      isLocationsDisabled,
    } = this.state

    return [
      !isWorkflowsDisabled && mobileRoutes.FORMS,
      mobileRoutes.NOTIFICATIONS,
      !isLocationsDisabled && mobileRoutes.LOCATIONS,
      !isEquipmentDisabled && mobileRoutes.EQUIPMENT,
      !isMaterialResourcesDisabled && mobileRoutes.MATERIAL_RESOURCES,
      !isStruxTagsDisabled && mobileRoutes.STRUXTAGS,
      !isDeliveriesDisabled && mobileRoutes.DELIVERIES,
    ].filter(route => !!route)
  }

  public goBack(
    altBackTransitionCb: () => void = this.displayDefaultView,
    steps: number = -1,
  ) {
    // presence 'key' indicates that 'history.push' calls have already been made within the current session
    if (this.history.location.key) {
      this.history.go(steps)
    } else {
      altBackTransitionCb()
    }
  }

  @action.bound
  public activityDetailsBackClicked() {
    this.history.goBack()
  }

  public displayActivityList() {
    return
  }

  public displayUsersDirectoryView = () => {
    this._displayView(mobileRoutes.USERS_DIRECTORY)
  }

  public displayChatView = () => {
    this._displayView(mobileRoutes.CHAT)
  }

  public displayUserProfileView = ({ id }: User) => {
    this._displayView(mobileRoutes.USER_PROFILE.replace(':userId', id))
  }

  /**
   * @param {string} id - If present, will open the notification with the given value in its `entityId`
   */
  public displayNotifications = (id?: string) => {
    this.showNavBar()
    this._displayView(mobileRoutes.NOTIFICATIONS, id ? { id } : {})
  }

  public displayProjects = () => {
    this.showNavBar()
    this._displayView(mobileRoutes.PROJECTS)
  }

  public openDeliveryView() {
    this.hideNavBar()
    this.displayDeliveriesView()
  }

  public displayForms() {
    this._displayView(mobileRoutes.FORMS)
  }

  public displayQRCodes() {
    this._displayView(mobileRoutes.QR_CODES)
  }

  public displayDeliveriesView = (viewMode?: string) => {
    this._displayView(mobileRoutes.DELIVERIES, { viewMode })
  }

  public displayEquipmentView = (params?: { action?: string }) => {
    this._displayView(mobileRoutes.EQUIPMENT, params)
  }

  public displayStruxTagsView = (params?: { action?: string }) => {
    this._displayView(mobileRoutes.STRUXTAGS, params)
  }

  public displayMaterialResourcesView = (params?: { action?: string }) => {
    this._displayView(mobileRoutes.MATERIAL_RESOURCES, params)
  }

  public displayLocationsView = (params?: { action?: string }) => {
    this._displayView(mobileRoutes.LOCATIONS, params)
  }

  public displayDeliveryDetailsView = (id: string) => {
    this.hideNavBar()
    this._displayView(
      mobileRoutes.DELIVERY_DETAILS.replace(
        getURLParam(mobileRoutes.DELIVERY_DETAILS),
        id,
      ),
    )
  }

  public displaySharedDeliveryView = (id: string) => {
    this.hideNavBar()
    this._displayView(
      mobileRoutes.SHARED_DELIVERY_DETAILS.replace(
        getURLParam(mobileRoutes.SHARED_DELIVERY_DETAILS),
        id,
      ),
    )
  }

  public displaySharedFormView = (id: string) => {
    this.hideNavBar()
    this._displayView(
      mobileRoutes.SHARED_FORM_DETAILS.replace(
        getURLParam(mobileRoutes.SHARED_FORM_DETAILS),
        id,
      ),
    )
  }

  public displayPhotos() {
    this._displayView(mobileRoutes.DOCUMENTS)
  }

  public displayNotificationDetails(notification: BaseNotification) {
    if (!notification) {
      return
    }

    const { entityId: id, type } = notification

    switch (true) {
      case isDeliveryType(type):
        return this.displayDeliveryDetailsView(id)

      case isPermitType(type):
      case isWorkflowMessageType(type):
        return this.displayFormView(id)

      case type === NotificationType.NewUserCreatedViaSignUp:
        return

      default:
        return
    }
  }

  public displayPhotoView(photo: Photo) {
    if (!photo) {
      return
    }
    this.hideNavBar()
    this._displayView(mobileRoutes.PHOTO_DETAIL.replace(':photoId', photo.id))
  }

  public displayFormView(id: string) {
    if (!id) {
      return
    }

    this.hideNavBar()
    const route = mobileRoutes.FORM_VIEW.replace(':formId', id)
    this._displayView(route)
  }

  public displayBulkStatusUpdateView() {
    return
  }

  public displayActivityDetailsView() {
    return
  }

  @action.bound
  public openChat() {
    this.displayChatView()
  }

  public get viewToDisplay(): string {
    const {
      isLogisticsDisabled,
      isDeliveriesDisabled,
      isWorkflowsDisabled,
      isEquipmentDisabled,
      isMaterialResourcesDisabled,
      isPhotosDisabled,
      isStruxTagsDisabled,
      isLocationsDisabled,
    } = this.state

    const defaultPage = this.defaultAvailablePage
    if (defaultPage) {
      return defaultPage
    }

    switch (true) {
      case !isLogisticsDisabled:
        return mobileRoutes.HOME
      case !isDeliveriesDisabled:
        return mobileRoutes.DELIVERIES
      case !isWorkflowsDisabled:
        return mobileRoutes.FORMS
      case !isEquipmentDisabled:
        return mobileRoutes.EQUIPMENT
      case !isMaterialResourcesDisabled:
        return mobileRoutes.MATERIAL_RESOURCES
      case !isPhotosDisabled:
        return mobileRoutes.DOCUMENTS
      case !isStruxTagsDisabled:
        return mobileRoutes.STRUXTAGS
      case !isLocationsDisabled:
        return mobileRoutes.LOCATIONS
      default:
        return mobileRoutes.NOTIFICATIONS
    }
  }

  public get defaultAvailablePage(): string | undefined {
    const {
      isLogisticsDisabled,
      isDeliveriesDisabled,
      isWorkflowsDisabled,
      isEquipmentDisabled,
      isMaterialResourcesDisabled,
      isPhotosDisabled,
      isLocationsDisabled,
      activeProject: { defaultLandingPage = DefaultLandingPage.Home },
    } = this.state

    switch (true) {
      case defaultLandingPage === DefaultLandingPage.Home &&
        !isLogisticsDisabled:
        return mobileRoutes.HOME
      case defaultLandingPage === DefaultLandingPage.Deliveries &&
        !isDeliveriesDisabled:
        return mobileRoutes.DELIVERIES
      case defaultLandingPage === DefaultLandingPage.Forms &&
        !isWorkflowsDisabled:
        return mobileRoutes.FORMS
      case defaultLandingPage === DefaultLandingPage.Equipment &&
        !isEquipmentDisabled:
        return mobileRoutes.EQUIPMENT
      case defaultLandingPage === DefaultLandingPage.MaterialResources &&
        !isMaterialResourcesDisabled:
        return mobileRoutes.MATERIAL_RESOURCES
      case defaultLandingPage === DefaultLandingPage.Photos &&
        !isPhotosDisabled:
        return mobileRoutes.DOCUMENTS
      case defaultLandingPage === DefaultLandingPage.Locations &&
        !isLocationsDisabled:
        return mobileRoutes.LOCATIONS
      default:
        return undefined
    }
  }
}
